import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, Popover, message, Modal, DatePicker } from "antd";

import { FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Instance from "../../AxiosConfig";
import { Form } from "react-bootstrap";

const GSTReportTable = () => {
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const hiddenContainerRef = useRef(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [currency, setCurrency] = useState("All");
  const [fromDate, setFromDate] = useState(null);
  const [totalGst, setTotalGst] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const [toDate, setToDate] = useState(null);
  const debounceTimer = useRef(null);

  const handleClearFilters = () => {
    setSearch("");
    setStatus("All");
    setCurrency("All");
    setFromDate(null);
    setToDate(null);
  };
  // Fetch filtered data with debounce
  const fetchFilteredData = async (
    page = pagination.current,
    limit = pagination.pageSize,
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (search.trim()) {
        params.append("search", search.trim());
      }

      if (status && status !== "All") {
        params.append("status", status);
      }

      if (currency && currency !== "All") {
        params.append("currency", currency);
      }

      if (fromDate) {
        params.append("startDate", fromDate);
      }

      if (toDate) {
        params.append("endDate", toDate);
      }

      const queryString = params.toString();
      const endpoint = queryString
        ? `/invoices/filter/search?${queryString}`
        : `/invoices`;

      const response = await Instance.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        const data = response?.data || {};
        const invoices = data.invoices || [];

        setInvoiceData(invoices);

        // 👇 THIS IS THE MAIN FIX
        if (data.total) {
          setPagination((prev) => ({
            ...prev,
            total: data.total,
            current: data.page || page,
          }));
        } else {
          // normal API (no pagination)
          setPagination((prev) => ({
            ...prev,
            total: invoices.length,
            current: 1,
          }));
        }

        const filtered = invoices.filter((inv) => inv.currency === "INR");

        setInvoiceData(filtered);
      } else {
        message.error("Failed to fetch invoice data");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  // Debounced filter function
  const debouncedFetch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchFilteredData();
    }, 500); // 500ms delay
  };

  const fetchTDSReport = async () => {
    try {
      const token = localStorage.getItem("token");

      let url = "/GstReport/gst-report";

      // If date filter applied → use filtered endpoint
      if (fromDate || toDate) {
        const params = new URLSearchParams();
        if (fromDate) params.append("startDate", fromDate);
        if (toDate) params.append("endDate", toDate);
        url = `/GstReport/gst-report?${params.toString()}`;
      }

      const response = await Instance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
console.log("GST Report Response:", response);
      const result = response?.data || {};

      // ⬅️ API gives direct total amount
      const total = Number(result.totalGSTAmount || 0);

      setTotalGst(total);
    } catch (err) {
      console.error("TDS Fetch Error:", err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchFilteredData();
  }, []);

  // Listen to filter changes
  useEffect(() => {
    debouncedFetch();
    fetchTDSReport();

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search, status, currency, fromDate, toDate]);

  const handleTaxInvoiceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Instance.get("/invoices", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        const invoices = response?.data?.invoices || [];

        const filtered = invoices.filter((inv) => inv.currency === "INR");

        setInvoiceData(filtered);

        console.log("Fetched invoices:", invoices);
      } else {
        message.error("Failed to fetch invoice data");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleTaxInvoiceData();
  }, []);

  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      fixed: "left",
      render: (val) => <strong>{val}</strong>,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Agent",
      key: "agentName",
      render: (_, record) => record?.agentId?.agentName || "—",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (currency) => (
        <Tag
          color={currency === "INR" ? "green" : "gold"}
          style={{ fontWeight: 500 }}
        >
          {currency}
        </Tag>
      ),
    },
    {
      title: "Total Closure",
      dataIndex: "baseClosureAmount",
      key: "baseClosureAmount",
      render: (val, record) =>
        `${record.currency} ${(val || 0).toLocaleString()}`,
    },
    {
      title: "Term Amount",
      render: (val, record) =>
        `${record.currency} ${record.terms[0]?.baseAmount}`,
    },
    {
      title: "Gst Amount",
      render: (record) => `${record.currency} ${record.TotalGSTAmount}`,
    },
    {
      title: "Money Received",
      dataIndex: "moneyReceived",
      key: "moneyReceived",
      render: (val, record) => (val ? `INR ${val.toLocaleString()}` : "—"),
    },
    {
      title: "Pending Payment (INR)",
      dataIndex: "PendingPaymentInINR",
      key: "PendingPaymentInINR",
      render: (val, record) => (
        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
          {record.currency} {Number(val || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Created On",
      dataIndex: "InvoiceDate",
      key: "createdAt",
      render: (val) => dayjs(val).format("DD MMM YYYY"),
    },
  ];

  return (
    <>
      <div className="filter-card p-3">
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by company or invoice no..."
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(date) =>
                    setFromDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(date) =>
                    setToDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="clear-btn-company w-100"
                onClick={handleClearFilters}
                style={{ height: "38px" }}
              >
                Clear
              </button>
            </div>
          </div>
        </Form>

        {(search ||
          status !== "All" ||
          currency !== "All" ||
          fromDate ||
          toDate) && (
          <div className="mt-3">
            <small className="text-muted">
              {loading
                ? "Loading filtered results..."
                : `Showing ${invoiceData.length} result(s)`}
            </small>
          </div>
        )}
      </div>
      <div className="d-flex justify-content-start my-3">
        <h4 style={{ fontWeight: "700" }}>
          Total GST Amount: INR {totalGst.toLocaleString()}
        </h4>
      </div>
      <div className="taxinvoice-wrapper mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontWeight: "600" }}>GST Reports</h2>
        </div>

        <Table
          columns={columns}
          dataSource={invoiceData}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
          }}
          onChange={(pag) => {
            setPagination(pag);
            fetchFilteredData(pag.current, pag.pageSize);
          }}
          scroll={{ x: "max-content" }}
          bordered
          rowKey={(record) => record._id}
        />

        <div
          ref={hiddenContainerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      </div>
    </>
  );
};

export default GSTReportTable;
